import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import AnimatedBackground from "../AnimatedBackground";
import PunchCardPreview from "../PunchCardPreview";
import MaterialIcon from "@/components/ui/MaterialIcon";
import Container from "../Container";
import Footer from "../Footer";

const LandingPage: React.FunctionComponent = () => {
  return (
    <>
      {/* First Section with Animated Background */}
      <AnimatedBackground fullHeight={false}>
        <Container className="pt-28 pb-48">
          {/* Hero Section */}
          <div className="mt-[10vh] mb-[10vh] max-w-4xl">
            <Heading level={1} variant="xl" className="mb-6 text-foreground">
              The scorecard for modern support teams.
            </Heading>
            <Text variant="xl" className="text-muted-foreground mb-8 max-w-2xl">
              Punch Cards delivers daily or weekly scores to every agent and manager in your customer support team. Your customised scorecard turns top performance into something the whole team can aim for.
            </Text>
            <div>
              <Button asChild variant="highlight" size="lg" className="text-lg px-8">
                <Link to="/waitlist">Join the Waitlist</Link>
              </Button>
            </div>
          </div>
        </Container>
      </AnimatedBackground>

      {/* Second Section - Features */}
      <section className="relative z-20 w-full bg-highlight pt-48 pb-24">
        {/* Scorecard Previews - Using negative margin */}
        <div className="relative -mt-96 mb-20">
          <Container>
            <div className="relative grid md:grid-cols-3 gap-6 items-center md:-ml-8 lg:-ml-12 md:w-[calc(100%+2rem)] lg:w-[calc(100%+3rem)]">
              {/* Left Card - Tucks under middle card on mobile */}
              <div className="relative z-0 -rotate-2 md:-right-8 -mb-12">
                <PunchCardPreview
                  agent={1}
                  manager={4}
                  repliesCount={18}
                  closedTicketsCount={3}
                  positiveCSATsCount={3}
                  message="Outstanding! Your response time has improved by 20%."
                />
              </div>

              {/* Center Card - Stays on top on mobile */}
              <div className="relative z-10">
                <PunchCardPreview
                  agent={3}
                  manager={4}
                  repliesCount={12}
                  closedTicketsCount={1}
                  positiveCSATsCount={1}
                  message="Great work yesterday! Seize the day today."
                />
              </div>

              {/* Right Card - Tucks under middle card on mobile */}
              <div className="relative z-0 rotate-2 md:-left-8 -mt-12 md:mt-12">
                <PunchCardPreview
                  agent={5}
                  manager={4}
                  repliesCount={24}
                  closedTicketsCount={4}
                  positiveCSATsCount={6}
                  message="Incredible! You're on fire today, keep it up! 🔥"
                />
              </div>
            </div>
          </Container>
        </div>

        <Container className="text-highlight-foreground">
          <Heading level={2} variant="lg" className="mb-8 max-w-4xl">The system that makes <br />support scalable.</Heading>
          <Text variant="xl" className="mb-16 max-w-4xl font-light">
            Scaling a support team requires a motivated team where successful behaviours are identified and rewarded. With Punch Cards, you&apos;ll know when your team are thriving.
          </Text>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {/* Feature 1 */}
            <div>
              <div className="w-12 h-12 mb-4 text-highlight-foreground flex items-center justify-start">
                <MaterialIcon icon="analytics" size={48} weight={200} />
              </div>
              <Text variant="lg">
                Analyse replies, tickets and CSAT scores from your customer support system
              </Text>
            </div>

            {/* Feature 2 */}
            <div>
              <div className="w-12 h-12 mb-4 text-highlight-foreground flex items-center justify-start">
                <MaterialIcon icon="inbox" size={48} weight={200} />
              </div>
              <Text variant="lg">
                Each agent receives a personalized scorecard in their messages
              </Text>
            </div>

            {/* Feature 3 */}
            <div>
              <div className="w-12 h-12 mb-4 text-highlight-foreground flex items-center justify-start">
                <MaterialIcon icon="tune" size={48} weight={200} />
              </div>
              <Text variant="lg">
                Tweak your own scoring system or use our industry-developed standard
              </Text>
            </div>

            {/* Feature 4 */}
            <div>
              <div className="w-12 h-12 mb-4 text-highlight-foreground flex items-center justify-start">
                <MaterialIcon icon="monitoring" size={48} weight={200} />
              </div>
              <Text variant="lg">
                Monitor your team progress and reward outstanding performers
              </Text>
            </div>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default LandingPage;
